import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import client from '../../api/client';

const STATUS_CONFIG = {
  open:            { label: 'Open',            badge: 'bg-blue-100 text-blue-700',      dot: 'bg-blue-500' },
  dispatched:      { label: 'Dispatched',      badge: 'bg-amber-100 text-amber-700',    dot: 'bg-amber-500' },
  faulty_received: { label: 'Faulty Received', badge: 'bg-orange-100 text-orange-700',  dot: 'bg-orange-500' },
  closed:          { label: 'Closed',          badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
};

export default function RMATickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const { data } = await client.get('/inventory/rma-tickets/', { params });
      setTickets(data.results || data);
    } catch { setTickets([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const filtered = tickets.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (t.ticket_number || '').toLowerCase().includes(q) ||
      (t.parent_ticket_number || '').toLowerCase().includes(q) ||
      (t.part_code || '').toLowerCase().includes(q) ||
      (t.device_name || '').toLowerCase().includes(q) ||
      (t.location_name || '').toLowerCase().includes(q);
  });

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-brand-500">RMA Tickets</h1>
        <p className="text-gray-400 text-sm mt-0.5">Supply chain replacement requests linked to incident tickets</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-44">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input className="input-field pl-9" placeholder="Search ticket, part code, location…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="input-field w-auto" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="dispatched">Dispatched</option>
          <option value="faulty_received">Faulty Received</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {/* Summary pills */}
      <div className="flex gap-3 flex-wrap">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const count = tickets.filter(t => t.status === key).length;
          return (
            <button key={key} onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                statusFilter === key ? `${cfg.badge} ring-1` : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
              }`}>
              {count} {cfg.label}
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{background: 'linear-gradient(to right, #F5F3FC, #EAE7F8)'}}>
                <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">RMA #</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Parent Ticket</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Location</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Part Code</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Device</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Dispatch Unit S/N</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Assigned To</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-50">
              {loading ? (
                <tr><td colSpan={9} className="text-center py-12 text-gray-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-16">
                  <p className="text-brand-500 font-semibold">No RMA tickets found</p>
                  <p className="text-gray-400 text-sm mt-1">RMA child tickets are auto-created when an incident ticket moves to RMA from Spares</p>
                </td></tr>
              ) : filtered.map(t => {
                const sc = STATUS_CONFIG[t.status] || STATUS_CONFIG.open;
                return (
                  <tr key={t.id} className="hover:bg-brand-50/40 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-brand-600">{t.ticket_number}</td>
                    <td className="px-4 py-3">
                      <Link to={`/tickets/${t.parent_ticket}`} className="font-mono text-xs text-brand-500 hover:underline">{t.parent_ticket_number}</Link>
                    </td>
                    <td className="px-4 py-3 text-gray-700 text-xs">
                      <div>{t.location_name}</div>
                      {t.sub_location && <div className="text-gray-400">{t.sub_location}</div>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-brand-600">{t.part_code || '—'}</td>
                    <td className="px-4 py-3 text-gray-700 text-xs">{t.device_name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">
                      {t.dispatch_unit_detail?.serial_number || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${sc.badge}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                        {sc.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{t.assigned_to_name || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/rma-tickets/${t.id}`} className="text-xs font-semibold text-brand-500 hover:text-brand-400">
                        View →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
